/**
 * Salzburg Geographic Boundaries and Location Validation Utilities
 * 
 * Defines bounding box coordinates, administrative regions, and helper
 * functions to determine if a location is strictly inside Salzburg (State or City).
 */

export interface LocationGeoData {
  title: string
  address: string
  lat?: number
  lng?: number
  isSalzburg?: boolean
  placeId?: string
}

// Bounding box for the entire federal state of Salzburg (Land Salzburg, Austria)
// South: Alps border with Carinthia (~46.90 N)
// North: Border with Upper Austria & Bavaria (~48.06 N)
// West: Krimml / Gerlos border with Tyrol (~12.05 E)
// East: Lungau / Styria border (~13.95 E)
export const SALZBURG_BOUNDS = {
  south: 46.90,
  north: 48.06,
  west: 12.05,
  east: 13.95,
}

// Center of Salzburg City
export const SALZBURG_CENTER = {
  lat: 47.8095,
  lng: 13.0550,
}

// Key municipalities, towns, valleys and districts within the Federal State of Salzburg
export const SALZBURG_DISTRICTS_AND_TOWNS = [
  'salzburg',
  'salzburger land',
  'land salzburg',
  'flachgau',
  'tennengau',
  'pongau',
  'pinzgau',
  'lungau',
  'salzburg-umgebung',
  'hallein',
  'zell am see',
  'kaprun',
  'st. johann im pongau',
  'sankt johann im pongau',
  'bischofshofen',
  'bad gastein',
  'bad hofgastein',
  'dorfgastein',
  'saalbach',
  'hinterglemm',
  'saalfelden',
  'flachau',
  'wagrain',
  'radstadt',
  'altenmarkt im pongau',
  'obertauern',
  'krimml',
  'mittersill',
  'neukirchen am großvenediger',
  'bruck an der großglocknerstraße',
  'grossglockner',
  'großglockner',
  'maria alm',
  'leogang',
  'lofer',
  'tamsweg',
  'mauterndorf',
  'st. michael im lungau',
  'wals-siezenheim',
  'wals',
  'siezenheim',
  'grödig',
  'groedig',
  'anif',
  'bergheim',
  'seekirchen am wallersee',
  'seekirchen',
  'neumarkt am wallersee',
  'straßwalchen',
  'strasswalchen',
  'eugendorf',
  'henndorf am wallersee',
  'mattsee',
  'oberndorf bei salzburg',
  'oberndorf',
  'fuschl am see',
  'fuschl',
  'st. gilgen',
  'sankt gilgen',
  'kuchl',
  'golling an der salzach',
  'golling',
  'abtenau',
  'puch bei hallein',
  'bad vigaun',
  'werfen',
  'werfenweng',
  'großarl',
  'grossarl',
  'hüttschlag',
]



/**
 * Checks whether coordinates fall within the Federal State of Salzburg bounds.
 */
export function areCoordsInSalzburg(lat: number, lng: number): boolean {
  return (
    lat >= SALZBURG_BOUNDS.south &&
    lat <= SALZBURG_BOUNDS.north &&
    lng >= SALZBURG_BOUNDS.west &&
    lng <= SALZBURG_BOUNDS.east
  )
}

/**
 * Checks if a location (by text description or coordinates) is inside the Federal State of Salzburg.
 */
export function isLocationInSalzburg(location: {
  title?: string
  address?: string
  lat?: number
  lng?: number
}): boolean {
  // 1. If explicit coordinates are provided, test against the bounding box
  if (
    typeof location.lat === 'number' &&
    typeof location.lng === 'number' &&
    !isNaN(location.lat) &&
    !isNaN(location.lng)
  ) {
    if (areCoordsInSalzburg(location.lat, location.lng)) {
      return true
    }
  }

  // 2. Test text matching against known Salzburg towns, districts, postal codes
  const combinedText = `${location.title || ''} ${location.address || ''}`.toLowerCase()

  if (!combinedText.trim()) return false

  // Direct mention of Salzburg
  if (combinedText.includes('salzburg')) {
    return true
  }

  // Austrian postal codes in Salzburg region (predominantly 50xx to 57xx)
  const austrianPostcodeMatch = combinedText.match(/\b(5[0-7]\d{2})\b/)
  if (austrianPostcodeMatch && combinedText.includes('austria')) {
    return true
  }

  // Check against Salzburg districts and towns list
  for (const town of SALZBURG_DISTRICTS_AND_TOWNS) {
    // Regex word boundary matching so "wals" doesn't match "cornwall"
    const regex = new RegExp(`\\b${town.replace('.', '\\.')}\\b`, 'i')
    if (regex.test(combinedText)) {
      return true
    }
  }

  return false
}
